/**
 * CONN-PROBE-3 — Card de Conectividade (diagnóstico do probe do device).
 *
 * Observabilidade APENAS. Consome o contrato tipado `conn` (ConnProbeOut do
 * backend F1/F2): NÃO lê last_hb_payload cru, NÃO renderiza signature/nonce/
 * BSSID cru, NÃO aciona nada operacional (relé/reboot/watchdog).
 *
 * Props:
 *   conn           — bloco tipado { lan, inet, dns } | null
 *   connSupported  — opcional; deriva de `conn` quando ausente
 *   rssi, wifiCh   — opcionais (mesmo payload seguro): Wi-Fi resumido
 */
import { connProbeView, stateBadgeClass, stateLabel } from "../lib/connProbe.js";

const TIP = {
  lan: "Rede local — ping ao gateway/LAN do device.",
  inet: "Internet — ping a um alvo externo (conectividade externa).",
  dns: "DNS — tempo de resolução de nomes.",
};

function TargetRow({ label, tip, view, dns = false }) {
  return (
    <div className="space-y-0.5">
      <div className="flex items-center justify-between gap-2">
        <span title={tip} className="cursor-help text-sm text-noc-text">
          {label}
        </span>
        <span className={view.badge}>{view.label}</span>
      </div>
      <div className="font-mono text-[11px] text-noc-muted">
        {view.rtt}
        {!dns && <span> · {view.loss}</span>}
        <span className="text-noc-faint"> · {view.age}</span>
      </div>
    </div>
  );
}

export default function ConnProbeCard({ conn, connSupported, rssi, wifiCh }) {
  const v = connProbeView(conn, connSupported);

  return (
    <section className="card-pad space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <h2 className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-noc-muted">
          Conectividade
        </h2>
        {v.supported && (
          <span className={stateBadgeClass(v.overall)}>{stateLabel(v.overall)}</span>
        )}
      </div>

      {!v.supported ? (
        <div className="text-noc-muted text-xs">
          Firmware sem diagnóstico de conectividade.
        </div>
      ) : (
        <div className="space-y-2.5">
          <TargetRow label="Rede local" tip={TIP.lan} view={v.lan} />
          <TargetRow label="Internet" tip={TIP.inet} view={v.inet} />
          <TargetRow label="DNS" tip={TIP.dns} view={v.dns} dns />

          {(Number.isFinite(Number(rssi)) || Number.isFinite(Number(wifiCh))) && (
            <div className="pt-2 border-t border-noc-border/60 flex items-center justify-between gap-2">
              <span className="text-[11px] uppercase tracking-[0.12em] text-noc-faint">
                Wi-Fi
              </span>
              <span className="font-mono text-[11px] text-noc-muted">
                {Number.isFinite(Number(rssi)) ? `${Number(rssi)} dBm` : "—"}
                {Number.isFinite(Number(wifiCh)) && (
                  <span className="text-noc-faint"> · canal {Number(wifiCh)}</span>
                )}
              </span>
            </div>
          )}

          <div className="text-[11px] text-noc-faint">
            Atualizado {v.lan.age !== "—" ? v.lan.age : v.inet.age}
          </div>
        </div>
      )}
    </section>
  );
}
